import { AACore, AAProvider, Account, ChainInfo, Transaction } from "./third";

export * from "./third";

export interface Config {
  projectId: string;
  clientKey: string;
  appId: string;
}

export interface Options {
  debug?: boolean;
  defaultChainId?: number /** default Ethereum 1 */;
  supportedChainIds?: number[] /** default $defaultSupportedChains */;
}

export interface CreateConfig {
  username?: string /** default GMID */;
  rp_id?: string /** default window.location.hostname */;
  origin?: string /** default window.location.origin */;
  userHandle?: string /** default uuid */;
}

export interface ConnectConfig {
  rp_id?: string /** default window.location.hostname */;
  origin?: string /** default window.location.origin */;
}

export const events = {
  connect: "connect",
  disconnect: "disconnect",
} as const;
export const walletEvents: string[] = [events.connect, events.disconnect];
type WalletEvents = typeof events;
export type WalletEventName = WalletEvents[keyof WalletEvents];

export abstract class GMCore {
  protected constructor() {}

  // base config methods
  public declare readonly isGMNetwork: boolean;
  public abstract get connected(): boolean;
  public abstract isAvailable(): Promise<boolean>;
  public abstract create(params?: CreateConfig): Promise<boolean>;
  public abstract connect(params?: ConnectConfig): Promise<boolean>;
  public abstract disconnect(): Promise<boolean>;
  public abstract get chainId(): number;
  public abstract get chainInfo(): ChainInfo;
  public abstract setChainId(chainId: number): boolean;
  public abstract getChainInfo(chainId: number): ChainInfo;
  public abstract get supportedChainIds(): number[];
  public abstract get supportedChains(): ChainInfo[];
  // aa base config methods
  public abstract get wallet(): AccountAbstraction | null | undefined;
  public abstract get provider(): AAProvider | null | undefined;

  // common wallet event methods
  public abstract on(
    eventName: WalletEventName | string,
    listener: (...args: any[]) => void
  ): void;
  public abstract removeListener(
    event: WalletEventName | string,
    listener: (...args: any[]) => void
  ): void;
}

export abstract class AccountAbstraction {
  public readonly isGMNetwork: boolean = true;

  protected constructor() {}

  // base config methods
  public abstract get core(): AACore;
  public abstract get provider(): AAProvider;
  public abstract get account(): Account;
  public abstract get address(): string;
  public abstract get owner(): string;
  public abstract get chainId(): number;
  public abstract get chainInfo(): ChainInfo;

  public abstract getAccount(): Promise<Account>;
  public abstract getAddress(): Promise<string>;
  public abstract getOwner(): Promise<string>;
  public abstract setChainId(chainId: number): boolean;
  public abstract getChainInfo(chainId: number): ChainInfo;
  public abstract sendTransaction(tx: Transaction): Promise<string>;
}

export interface WindowWithGMNetwork {
  gmnetwork?: GMCore | null | undefined;
}

export const defaultSupportedChains: {
  // Testnet
  sepolia: number;
  gmTestnet: number;

  // Mainnet
  ethereum: number;
  polygon: number;
  bsc: number;
  avalanche: number;
  arbitrum: number;
  optimism: number;
  gmMainnet: number;

  [key: string]: number;
} = {
  // Testnet
  sepolia: 11155111,
  gmTestnet: 202402181627,

  // Mainnet
  ethereum: 1,
  polygon: 137,
  bsc: 56,
  avalanche: 43114,
  arbitrum: 42161,
  optimism: 10,
  gmMainnet: 2777,
};
